import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { resolveResourceUrl } from '../utils/resourceUrl';

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  const textValue = String(value);
  const numericValue = Number(textValue);
  if (Number.isFinite(numericValue) && Number.isInteger(numericValue)) {
    return String(numericValue);
  }

  return textValue;
};

const scrollElementToGuideOffset = (element) => {
  if (!element) {
    return;
  }

  const taskbarHeight =
    Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--taskbar-height')) || 72;
  const targetTop = element.getBoundingClientRect().top + window.scrollY - taskbarHeight - 24;
  window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
};

function AircraftGuidePage() {
  const guideTopRef = useRef(null);
  const pendingRestoreTypeRef = useRef('');
  const typeCardRefs = useRef({});
  const [aircraft, setAircraft] = useState([]);
  const [typeGuides, setTypeGuides] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadAircraft = async () => {
      try {
        const [aircraftResponse, typeGuideResponse] = await Promise.all([
          axios.get('/aircraft'),
          axios.get('/aircraft/type-guides'),
        ]);
        if (isMounted) {
          setAircraft(aircraftResponse.data);
          setTypeGuides(typeGuideResponse.data);
          setError('');
        }
      } catch {
        if (isMounted) {
          setError('Unable to load aircraft data right now.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void loadAircraft();
    return () => {
      isMounted = false;
    };
  }, []);

  const aircraftByType = useMemo(() => {
    return aircraft.reduce((groups, item) => {
      const aircraftType = item.aircraft_type || 'Other Aircraft';
      if (!groups[aircraftType]) {
        groups[aircraftType] = [];
      }
      groups[aircraftType].push(item);
      return groups;
    }, {});
  }, [aircraft]);

  const guideByType = useMemo(() => {
    return typeGuides.reduce((guides, guide) => {
      guides[guide.aircraft_type] = guide;
      return guides;
    }, {});
  }, [typeGuides]);

  const visibleTypeGuides = useMemo(() => {
    if (typeGuides.length > 0) {
      return typeGuides;
    }

    return Object.keys(aircraftByType).map((aircraftType) => ({
      aircraft_type: aircraftType,
      active_count: aircraftByType[aircraftType].length,
      description: [],
      sketch_path: aircraftByType[aircraftType][0]?.sketch_path || null,
    }));
  }, [aircraftByType, typeGuides]);

  const selectedGuide = selectedType ? guideByType[selectedType] : null;
  const selectedAircraft = selectedType ? aircraftByType[selectedType] || [] : [];

  useEffect(() => {
    if (selectedType || !pendingRestoreTypeRef.current) {
      return;
    }

    const restoreType = pendingRestoreTypeRef.current;
    pendingRestoreTypeRef.current = '';
    window.requestAnimationFrame(() => {
      scrollElementToGuideOffset(typeCardRefs.current[restoreType] || guideTopRef.current);
    });
  }, [selectedType]);

  const scrollToGuideTop = () => {
    window.requestAnimationFrame(() => {
      scrollElementToGuideOffset(guideTopRef.current);
    });
  };

  const showAircraftType = (aircraftType) => {
    setSelectedType(aircraftType);
    scrollToGuideTop();
  };

  const showAircraftTypes = () => {
    pendingRestoreTypeRef.current = selectedType;
    setSelectedType('');
  };

  return (
    <div className="inner-page">
      <section className="inner-page-hero" style={{ '--inner-hero-image': "url('/assets/aircraft-guide.jpg')" }}>
        <p className="inner-page-eyebrow">Fleet Reference</p>
        <h1>Aircraft Guide</h1>
        <p>Aircraft currently available in our network.</p>
      </section>

      <section className="page-shell" ref={guideTopRef}>
        <div className="aircraft-guide-shell">
          {loading && <p className="status-note">Loading aircraft list...</p>}
          {!loading && error && <p className="status-note status-note--error">{error}</p>}

          {!loading && !error && !selectedType && (
            <div className="aircraft-type-list">
              {visibleTypeGuides.map((guide, index) => (
                <article
                  key={guide.aircraft_type}
                  ref={(node) => {
                    if (node) {
                      typeCardRefs.current[guide.aircraft_type] = node;
                    } else {
                      delete typeCardRefs.current[guide.aircraft_type];
                    }
                  }}
                  className={`aircraft-type-feature ${index % 2 === 1 ? 'aircraft-type-feature--reversed' : ''}`}
                >
                  <div className="aircraft-type-copy">
                    <p className="aircraft-type-eyebrow">{guide.active_count} active aircraft</p>
                    <h2>{guide.aircraft_type}</h2>
                    {guide.description.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                    <button className="aircraft-action" type="button" onClick={() => showAircraftType(guide.aircraft_type)}>
                      See {guide.aircraft_type} Options
                    </button>
                  </div>

                  {guide.sketch_path && (
                    <div className="aircraft-type-media">
                      <img src={resolveResourceUrl(guide.sketch_path)} alt={`${guide.aircraft_type} sketch`} />
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}

          {!loading && !error && selectedType && (
            <div className="aircraft-detail-view">
              <button className="aircraft-back-button" type="button" onClick={showAircraftTypes}>
                Back to aircraft types
              </button>

              <div className="aircraft-detail-heading">
                <p className="aircraft-type-eyebrow">{selectedAircraft.length} active aircraft</p>
                <h2>{selectedType} Options</h2>
                {selectedGuide?.description.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>

              <div className="aircraft-detail-list">
                {selectedAircraft.map((item) => {
                  const imagePath = item.image_path || item.sketch_path;
                  return (
                    <article key={item.id} className="aircraft-detail-card">
                      {imagePath && (
                        <div className={`aircraft-detail-media ${item.image_path ? '' : 'aircraft-detail-media--sketch'}`}>
                          <img src={resolveResourceUrl(imagePath)} alt={item.model || 'Aircraft'} />
                        </div>
                      )}

                      <div className="aircraft-detail-copy">
                        <h3>{item.model || 'Unknown Model'}</h3>
                        <dl>
                          <div>
                            <dt>Seats</dt>
                            <dd>{item.pax ? `${formatValue(item.pax)} seats` : '-'}</dd>
                          </div>
                          <div>
                            <dt>Range</dt>
                            <dd>{item.distance_km ? `${formatValue(item.distance_km)} km` : '-'}</dd>
                          </div>
                          <div>
                            <dt>Cruise speed</dt>
                            <dd>{item.cruise_tas_kmh ? `${formatValue(item.cruise_tas_kmh)} km/h` : '-'}</dd>
                          </div>
                        </dl>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default AircraftGuidePage;
